<script setup lang="ts">
import { computed, watch } from "vue";

import LeCompactFooter from "@/components/articles/LeCompactFooter.vue";
import { DictsysInflectionSchemeHelper } from "@/helpers";
import { useDictionariesStore, useUserStore } from "@/stores";
import type { DictsysArticle, SearchViewType } from "@/types";

import LeArticleBody from "./LeArticleBody.vue";
import LeArticleHeader from "./LeArticleHeader.vue";
import LeArticlePrefix from "./LeArticlePrefix.vue";

const props = defineProps<{
  article: DictsysArticle;
  searchViewType: SearchViewType;
}>();

const dictionariesStore = useDictionariesStore();
const userStore = useUserStore();

const method = computed(() => dictionariesStore.getMethodByMethodId(props.article.methodId));
const dictionary = computed(() => dictionariesStore.getDictionaryByMethodId(props.article.methodId));

const isCompactSearchView = computed<boolean>(() => props.searchViewType === "compact");
const isCompactArticlesExpanded = computed<boolean>(() => userStore.userData.settings.compactArticlesExpanded);

// Unwrap rawJson inflection schemes
watch(
  () => props.article.id,
  () => {
    const data = props.article.article;
    DictsysInflectionSchemeHelper.parseJsonInflectionScheme(data.inflectionScheme);

    data.meanings.forEach((meaning) => {
      meaning.translations.forEach((translation) => {
        DictsysInflectionSchemeHelper.parseJsonInflectionScheme(translation.inflectionScheme);
      });

      meaning.combinations.forEach((combination) => {
        DictsysInflectionSchemeHelper.parseJsonInflectionScheme(combination.inflectionScheme);
      });
    });
  },
  { immediate: true }
);
</script>

<template>
  <article class="le-article">
    <LeArticlePrefix :article="props.article" />
    <LeArticleHeader
      class="le-article__header"
      :article="props.article"
      :force-inline-buttons="isCompactSearchView"
      :with-dictionary-cover="isCompactSearchView"
    />
    <LeArticleBody :article="props.article" :collapsible="isCompactSearchView" :expanded="isCompactArticlesExpanded" />
    <LeCompactFooter v-if="isCompactSearchView && method && dictionary" :method="method" :dictionary="dictionary" />
  </article>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-article {
  border-radius: 4px;
  background-color: @colors[@neutrals][100];
  box-shadow: @shadows[floating-default];

  &__header {
    border-bottom: 2px solid @colors[@secondary][300];
  }
}
</style>
